import React from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import Alert from '@material-ui/lab/Alert';
import {makeStyles} from '@material-ui/core/styles';
import Badge from '@material-ui/core/Badge';
import TimerIcon from '@material-ui/icons/Timer';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import PowerIcon from '@material-ui/icons/Power';
import BugReportIcon from '@material-ui/icons/BugReport';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import GpsFixedIcon from '@material-ui/icons/GpsFixed';
import MemoryIcon from '@material-ui/icons/Memory';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import CloudOffIcon from '@material-ui/icons/CloudOff';
import NetworkCheckIcon from '@material-ui/icons/NetworkCheck';

const useStyles = makeStyles({
  tooltip: {
    backgroundColor: 'transparent',
    boxShadow: '0 0 50px rgba(0, 0, 0, 0.2)',
    padding: 0,
    '& .MuiAlert-root': {
      margin: 0,
    },
  },
  insightsWrapper: {
    display: 'inline-flex',
    position: 'absolute',
    right: 10,
    top: '45%',
    transform: 'translateY(-50%)',
    alignItems: 'center',
  },
  tooltipContent: {
    padding: 8,
    '& > div': {
      marginBottom: 8,
      '&:last-child': {
        marginBottom: 0,
      },
    },
  },
});

const iconMap = {
  Timer: TimerIcon,
  TrendingUp: TrendingUpIcon,
  Power: PowerIcon,
  BugReport: BugReportIcon,
  DeleteForever: DeleteForeverIcon,
  GpsFixed: GpsFixedIcon,
  Memory: MemoryIcon,
  MonetizationOn: MonetizationOnIcon,
  CloudOff: CloudOffIcon,
  NetworkCheck: NetworkCheckIcon,
};

const severityLevels = {
  error: 0,
  warning: 1,
  success: 2,
};

const AnalyticsInsights = ({insights, onMouseEnter, onMouseLeave, isSourceActive, source}) => {
  const classes = useStyles();

  // For analytics/prebid, insights structure is: { [bidder]: { [issueType]: { icon, severity, ... } } }
  // We need to get insights for the current source (bidder)
  const sourceInsights = insights?.[source];

  if (!sourceInsights || Object.keys(sourceInsights).length === 0) return null;

  // Get the highest severity issue using waterfall priority: error > warning > success
  const issues = Object.values(sourceInsights);
  const highestSeverityIssue = issues.reduce((max, issue) => {
    const maxLevel = severityLevels[max.severity] ?? 999;
    const currentLevel = severityLevels[issue.severity] ?? 999;
    return currentLevel < maxLevel ? issue : max;
  }, issues[0]);

  const IconComponent = iconMap[highestSeverityIssue.icon] || TimerIcon;

  // Build tooltip content with only the highest priority issue
  const tooltipContent = (
    <Alert severity={highestSeverityIssue.severity} icon={false}>
      <div>
        {highestSeverityIssue.title && (
          <div style={{fontWeight: 'bold', fontSize: 14, marginBottom: 6}}>
            {highestSeverityIssue.title}
          </div>
        )}
        <div style={{marginBottom: 6}}>{highestSeverityIssue.interpretation}</div>
        {highestSeverityIssue.action && (
          <div style={{fontSize: 14, opacity: 1}}>
            <span style={{fontWeight: 'bold', display: 'inline'}}>Recommendation:</span>{' '}
            {highestSeverityIssue.action}
          </div>
        )}
      </div>
    </Alert>
  );

  return (
    <div
      className={classes.insightsWrapper}
      onMouseEnter={() => (isSourceActive ? onMouseEnter(highestSeverityIssue) : null)}
      onMouseLeave={onMouseLeave}
    >
      <Tooltip
        interactive
        title={tooltipContent}
        classes={{
          tooltip: classes.tooltip,
        }}
        arrow
      >
        <IconComponent
          style={{
            color:
              highestSeverityIssue.severity === 'error'
                ? 'red'
                : highestSeverityIssue.severity === 'warning'
                  ? 'orange'
                  : '#56cb80',
          }}
        />
      </Tooltip>
    </div>
  );
};

export default AnalyticsInsights;
